import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import { CheckCircle, Star } from 'lucide-react';
import { mockData } from '../data/mockData';

const PricingSection = ({ onContactClick }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const PricingCard = ({ plan, index }) => (
    <Card
      className={`p-6 h-full flex flex-col ${
        plan.recommended
          ? 'border-2 border-amber-500 shadow-2xl relative'
          : 'border shadow-lg'
      }`}
    >
      {plan.recommended && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center space-x-1">
            <Star size={14} fill="white" />
            <span>RECOMMENDED</span>
          </div>
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-blue-900 mb-2">{plan.name}</h3>
        <div className="mb-2">
          <span className="text-4xl font-bold text-blue-900">{plan.price}</span>
        </div>
        <p className="text-gray-600 font-medium">{plan.subtitle}</p>
      </div>

      <div className="flex-1 mb-6">
        <div className="space-y-3 max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {plan.features.map((feature, idx) => (
            <div key={idx} className="flex items-start space-x-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span className="text-gray-700 text-sm">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs text-gray-500 text-center mb-4">{plan.note}</p>
        <Button
          onClick={onContactClick}
          className={`w-full ${
            plan.recommended
              ? 'bg-amber-500 hover:bg-amber-600'
              : 'bg-blue-900 hover:bg-blue-800'
          } text-white py-6 text-lg font-semibold`}
        >
          Get Started
        </Button>
      </div>
    </Card>
  );

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">
              Transparent Trademark Registration Pricing
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the package that best fits your trademark protection needs. All plans include expert filing with the USPTO.
            </p>
          </div>

          {isMobile ? (
            // Mobile: Carousel View
            <div className="max-w-md mx-auto">
              <Carousel className="w-full">
                <CarouselContent>
                  {mockData.pricing.map((plan, index) => (
                    <CarouselItem key={index}>
                      <div className="p-1">
                        <PricingCard plan={plan} index={index} />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
              <div className="text-center mt-4 text-sm text-gray-500">
                Swipe to see all plans
              </div>
            </div>
          ) : (
            // Desktop: Grid View
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockData.pricing.map((plan, index) => (
                <PricingCard key={index} plan={plan} index={index} />
              ))}
            </div>
          )}

          <div className="mt-12 bg-white rounded-xl p-8 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-blue-900 mb-4 text-center">
              What's Not Included: USPTO Government Filing Fees
            </h3>
            <p className="text-gray-600 text-center mb-6">
              In addition to our service fee, you'll need to pay the USPTO government filing fee directly to the United States Patent and Trademark Office. Current fees range from $250-$350 per class of goods/services.
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start space-x-2">
                <CheckCircle className="text-blue-900 flex-shrink-0 mt-0.5" size={16} />
                <span className="text-gray-700">TEAS Plus: $250 per class (strictest requirements)</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="text-blue-900 flex-shrink-0 mt-0.5" size={16} />
                <span className="text-gray-700">TEAS Standard: $350 per class (more flexibility)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
